/**
 * Width-only window resize listeners.
 *
 * Mobile browsers fire `resize` every time their toolbar collapses or expands
 * while the page scrolls. Nothing these handlers care about (line wrapping,
 * column widths, slider measurements) changes then, but reverting a SplitText
 * heading or re-measuring a layout mid-scroll shows up as a scroll stall.
 * These helpers call the handler only when the viewport width has changed.
 *
 * @example
 *   onWidthResize(this.onResize);
 *   offWidthResize(this.onResize);
 *   onWidthResize(() => split.revert(), { once: true });
 */
const wrappers = new WeakMap();

/**
 * @param {(event: Event) => void} handler
 * @param {{ once?: boolean }} [options] - `once` removes the listener after the first width change.
 * @returns {() => void} Tear-down function (same as calling offWidthResize).
 */
export function onWidthResize(handler, { once = false } = {}) {
  offWidthResize(handler);
  let width = window.innerWidth;
  const wrapper = (event) => {
    if (window.innerWidth === width) return;
    width = window.innerWidth;
    if (once) offWidthResize(handler);
    handler(event);
  };
  wrappers.set(handler, wrapper);
  window.addEventListener('resize', wrapper);
  return () => offWidthResize(handler);
}

/** @param {(event: Event) => void} handler */
export function offWidthResize(handler) {
  const wrapper = wrappers.get(handler);
  if (!wrapper) return;
  wrappers.delete(handler);
  window.removeEventListener('resize', wrapper);
}
